// BUILD-042 Activity log (HR Administration > Oversight > Activity log).
//
// History: the 2026-10-05 usability pass gave this screen compact single-line rows with expandable
// details and local date/time. The Owner-approved improvement (decision "October 14 09:04 Phoenix")
// keeps that look and adds what an administrator needs to find "who did what, to whom, when":
//   - search by affected employee, acting user and keywords,
//   - filter by a recorded action and an inclusive Arizona (America/Phoenix) date range,
//   - server-side paging across the WHOLE authorized history (the old screen only ever loaded the
//     newest 200 rows, so older activity was simply unreachable),
//   - clear filters, and honest loading / empty / error states.
//
// What did NOT change: which events a person may see, which fields are shown (structural fields
// plus human labels only - never the stored before/after content, IP address or request id), and the
// fact that the log is a read-only history. Filtering and paging happen on the server, after the
// server has applied the caller's access scope; this component only builds the request and
// presents the answer.
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { getHrAudit, getHrAuditActions, listHrAdminEmployees, type HrAuditEventRow } from '../../api/client';
import { formatPhoenixDateTimeMinute, formatPhoenixDateTimeSeconds } from './hrFormat';
import { CollapsibleHelp } from './CollapsibleHelp';
import { SectionHeading, CardTitle } from './workspace/SectionHeading';
import { adminSubtitleFor } from './workspace/adminNavGroups';
import {
  EMPTY_FILTERS, PAGE_SIZES, DEFAULT_PAGE_SIZE, hasActiveFilters, normalizeFilters, validateFilters,
  buildAuditQuery, describeDateRange, actorLabel, employeeLabel, accessTypeLabel, shortRef,
  pageCount, describeRange, nextStack, previousStack, type ActivityFilters,
} from './activityLogModel';

interface ActionOption { action: string; label: string; count: number }

const inputClass = 'border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white';
const labelClass = 'text-xs text-gray-500 block mb-1';

export function AuditPanel() {
  const ids = { employee: useId(), actor: useId(), keyword: useId(), action: useId(), from: useId(), to: useId(), size: useId(), people: useId() };

  // What is typed in the boxes (draft) vs. what has actually been searched (applied).
  const [draft, setDraft] = useState<ActivityFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<ActivityFilters>(EMPTY_FILTERS);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);

  const [rows, setRows] = useState<HrAuditEventRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [namesAvailable, setNamesAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formProblem, setFormProblem] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [actions, setActions] = useState<ActionOption[]>([]);
  const [actionsError, setActionsError] = useState(false);
  const [activePeople, setActivePeople] = useState<string[]>([]);
  const requestId = useRef(0);

  // Suggestions for the two person boxes: the active employees from the same HR employee list
  // the Employees screen already uses (same permission gate, so a caller who cannot see names
  // gets no suggestions). These are shortcuts only; typing any name still searches the whole
  // history, which keeps former employees reachable.
  useEffect(() => {
    listHrAdminEmployees()
      .then((d) => {
        const names = d.employees
          .filter((e) => e.globalStatus === 'active' && e.name && e.name.trim())
          .map((e) => e.name!.trim());
        setActivePeople(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setActivePeople([]));
  }, []);

  // The Action dropdown lists only actions that really exist in this person's authorized history.
  useEffect(() => {
    getHrAuditActions()
      .then((d) => setActions(d.actions))
      .catch(() => setActionsError(true));
  }, []);

  const load = useCallback(() => {
    const mine = ++requestId.current;
    setLoading(true);
    setError(null);
    getHrAudit(buildAuditQuery(applied, pageSize, cursors[cursors.length - 1]))
      .then((d) => {
        if (mine !== requestId.current) return; // a newer request superseded this one
        setRows(d.events);
        setTotal(d.page.total);
        setHasMore(d.page.hasMore);
        setNextCursor(d.page.nextCursor);
        setNamesAvailable(d.namesAvailable);
        setExpanded(null);
        setLoading(false);
      })
      .catch((e: any) => {
        if (mine !== requestId.current) return;
        setError(e?.message || 'The activity log could not be loaded.');
        setLoading(false);
      });
  }, [applied, pageSize, cursors]);

  useEffect(() => { load(); }, [load]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = normalizeFilters(draft);
    const problem = validateFilters(next);
    setFormProblem(problem);
    if (problem) return;
    setDraft(next);
    setApplied(next);
    setCursors([null]); // any change of filters starts again at the newest page
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setFormProblem(null);
    setCursors([null]);
  }

  const filtered = hasActiveFilters(applied);
  const pageIndex = cursors.length - 1;
  const range = describeDateRange(applied);
  const shown = rows?.length ?? 0;
  const dirty = JSON.stringify(normalizeFilters(draft)) !== JSON.stringify(applied);

  return (
    <div>
      <SectionHeading title="Activity log" subtitle={adminSubtitleFor('audit')} />
      <div className="space-y-3">
        <form onSubmit={submit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" aria-label="Find activity" data-testid="activity-filters">
          <CardTitle>Find activity</CardTitle>
          {activePeople.length > 0 && (
            <datalist id={ids.people}>
              {activePeople.map((n) => <option key={n} value={n} />)}
            </datalist>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label htmlFor={ids.employee} className={labelClass}>Employee affected</label>
              <input id={ids.employee} className={inputClass} value={draft.employee} maxLength={100}
                list={activePeople.length > 0 ? ids.people : undefined} autoComplete="off"
                onChange={(e) => setDraft({ ...draft, employee: e.target.value })}
                placeholder={namesAvailable ? 'Pick or type a name' : 'Reference ID only'} />
            </div>
            <div>
              <label htmlFor={ids.actor} className={labelClass}>Done by</label>
              <input id={ids.actor} className={inputClass} value={draft.actor} maxLength={100}
                list={activePeople.length > 0 ? ids.people : undefined} autoComplete="off"
                onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
                placeholder={namesAvailable ? 'Pick or type a name' : 'Reference ID only'} />
            </div>
            <div>
              <label htmlFor={ids.keyword} className={labelClass}>Keywords</label>
              <input id={ids.keyword} className={inputClass} value={draft.keyword} maxLength={100}
                onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
                placeholder="e.g. document, checklist, grant" />
            </div>
            <div>
              <label htmlFor={ids.action} className={labelClass}>Action</label>
              <select id={ids.action} className={inputClass} value={draft.action}
                onChange={(e) => setDraft({ ...draft, action: e.target.value })}>
                <option value="">All recorded actions</option>
                {actions.map((a) => (
                  <option key={a.action} value={a.action}>{a.label} ({a.count})</option>
                ))}
              </select>
              {actionsError && <p className="text-[11px] text-amber-700 mt-1">The list of actions could not be loaded. You can still search.</p>}
            </div>
            <div>
              <label htmlFor={ids.from} className={labelClass}>From date (Arizona time)</label>
              <input id={ids.from} type="date" className={inputClass} value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
            </div>
            <div>
              <label htmlFor={ids.to} className={labelClass}>To date (Arizona time)</label>
              <input id={ids.to} type="date" className={inputClass} value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
            </div>
          </div>
          {formProblem && <p role="alert" className="text-xs text-red-700 mt-2" data-testid="filter-problem">{formProblem}</p>}
          <div className="flex items-center gap-2 mt-3">
            <button type="submit" className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded">Search</button>
            <button type="button" onClick={clearFilters} disabled={!filtered && !hasActiveFilters(draft)}
              className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Clear filters
            </button>
            {dirty && <span className="text-xs text-gray-400">Press Search to apply your changes.</span>}
          </div>
        </form>

        {error ? (
          <div role="alert" data-testid="activity-error" className="bg-white rounded-lg border border-red-200 shadow-sm p-5 text-sm">
            <div className="text-red-700">{error}</div>
            <button type="button" onClick={load} className="mt-3 text-sm text-[#4AABDE] hover:underline">Try again</button>
          </div>
        ) : rows === null || (rows.length === 0 && loading) ? (
          <div className="text-gray-500 text-sm" data-testid="activity-loading" role="status">Loading activityâ€¦</div>
        ) : rows.length === 0 && !loading ? (
          <div data-testid="activity-empty" className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-600">
            {filtered ? (
              <>
                <p>No activity matches these filters.</p>
                <button type="button" onClick={clearFilters} className="mt-2 text-sm text-[#4AABDE] hover:underline">Clear filters</button>
              </>
            ) : (
              <p className="text-gray-500">
                No activity to show yet. Every consequential write (record creation, employment changes,
                access grants/revokes, checklist and document actions) is recorded server-side; it will
                appear here once something happens.
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" aria-busy={loading}>
            <CardTitle>{filtered ? 'Matching activity' : 'Recent activity'}</CardTitle>
            <p className="text-xs text-gray-500 mb-2" role="status" data-testid="activity-summary">
              Showing {describeRange(pageIndex, pageSize, shown, total)} {total === 1 ? 'event' : 'events'}
              {filtered ? ' matching your search' : ''}
              {range ? ` Â· ${range}` : ''}
              {loading ? ' Â· updatingâ€¦' : ''}
            </p>
            {!namesAvailable && (
              <p className="text-[11px] text-gray-500 mb-2">Employee names are shown to HR administrators only, so people appear by reference.</p>
            )}
            <div className={`divide-y divide-gray-100 -mx-5 ${loading ? 'opacity-60' : ''}`} data-testid="activity-rows">
              {rows.map((ev, i) => {
                const isOpen = expanded === i;
                const subject = employeeLabel(ev);
                return (
                  <div key={`${ev.createdAt}-${i}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-gray-50"
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        className={`w-3 h-3 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        fill="currentColor"
                      >
                        <path d="M7 5l6 5-6 5V5z" />
                      </svg>
                      <span className="min-w-0 truncate flex-1">
                        <span className="font-medium text-gray-900">{actorLabel(ev)}</span>
                        <span className="text-gray-500"> {ev.actionLabel}</span>
                        {subject && <span className="text-gray-500"> for <span className="font-medium text-gray-900">{subject}</span></span>}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatPhoenixDateTimeMinute(ev.createdAt)}</span>
                    </button>
                    {isOpen && (
                      <dl className="px-4 pb-3 pt-1 text-xs bg-gray-50 grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1" data-testid="activity-details">
                        <dt className="text-gray-400">Done by</dt><dd className="text-gray-700">{actorLabel(ev)} <span className="text-gray-400">({shortRef(ev.actorEmployeeId)})</span></dd>
                        <dt className="text-gray-400">Employee affected</dt>
                        <dd className="text-gray-700">{subject ? <>{subject} <span className="text-gray-400">({shortRef(ev.subjectEmployeeId)})</span></> : 'No specific employee'}</dd>
                        <dt className="text-gray-400">What happened</dt><dd className="text-gray-700">{ev.actionLabel}</dd>
                        <dt className="text-gray-400">Item</dt><dd className="text-gray-700">{ev.entityLabel}{ev.entityId ? <span className="text-gray-400"> ({shortRef(ev.entityId)})</span> : null}</dd>
                        <dt className="text-gray-400">Kind of activity</dt><dd className="text-gray-700">{accessTypeLabel(ev.accessType)}</dd>
                        <dt className="text-gray-400">Date/time (Arizona)</dt><dd className="text-gray-700">{formatPhoenixDateTimeSeconds(ev.createdAt)}</dd>
                      </dl>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button type="button" disabled={pageIndex === 0 || loading}
                  onClick={() => setCursors(previousStack(cursors))}
                  className="text-sm px-3 py-1 rounded border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                  â€¹ Newer
                </button>
                <span className="text-xs text-gray-500" data-testid="activity-page">Page {pageIndex + 1} of {pageCount(total, pageSize)}</span>
                <button type="button" disabled={!hasMore || loading}
                  onClick={() => setCursors(nextStack(cursors, nextCursor))}
                  className="text-sm px-3 py-1 rounded border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                  Older â€º
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor={ids.size} className="text-xs text-gray-500">Per page</label>
                <select id={ids.size} value={pageSize} className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCursors([null]); }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
        <CollapsibleHelp>
          <p>This is a record of consequential HR changes: who did what, to which employee or item, and when.</p>
          <p>It cannot be edited or deleted. Search covers the whole history you are allowed to see, not just the latest entries. Date filters use whole days in Arizona time (the start and end dates are both included); the times shown on each row are in your browser's local time zone.</p>
        </CollapsibleHelp>
      </div>
    </div>
  );
}
